<div class="container">

  <header class="page-heading">
    <h1>CAGR Calculator</h1>
    <p>Calculate the Compound Annual Growth Rate (CAGR) of an investment from its initial value, final value and duration.</p>
  </header>

  <div class="card mb-lg">
    <div class="section-title">CAGR Calculator</div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('initialValue')">
        <label class="field-label" for="initialValue">Initial Value</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="initialValue" type="number"
            [(ngModel)]="initialValue"
            [min]="C.value.min" [max]="C.value.max" [step]="C.value.step"
            (ngModelChange)="onFieldChange('initialValue')"
            (blur)="onFieldBlur('initialValue')"
            placeholder="e.g. 1,00,000"
            autocomplete="off"/>
        </div>
        @if (hasError('initialValue')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('initialValue') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('finalValue')">
        <label class="field-label" for="finalValue">Final Value</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="finalValue" type="number"
            [(ngModel)]="finalValue"
            [min]="C.value.min" [max]="C.value.max" [step]="C.value.step"
            (ngModelChange)="onFieldChange('finalValue')"
            (blur)="onFieldBlur('finalValue')"
            placeholder="e.g. 2,00,000"
            autocomplete="off"/>
        </div>
        @if (hasError('finalValue')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('finalValue') }}
          </div>
        }
      </div>
    </div>

    <div class="field-group" [class.has-error]="hasError('years')">
      <label class="field-label" for="years">Duration</label>
      <div class="input-shell suffix">
        <input id="years" type="number"
          [(ngModel)]="years"
          [min]="C.years.min" [max]="C.years.max" [step]="C.years.step"
          (ngModelChange)="onFieldChange('years')"
          (blur)="onFieldBlur('years')"
          placeholder="e.g. 5"
          autocomplete="off"/>
        <span class="affix right">Yr</span>
      </div>
      @if (hasError('years')) {
        <div class="field-error">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {{ fieldError('years') }}
        </div>
      }
    </div>

    <div class="action-row">
      <button type="button" class="btn btn-primary" (click)="calculate()" [class.btn-disabled]="showFormErrors && !isFormValid">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        Calculate
      </button>
      @if (calculated) {
        <button type="button" class="btn btn-secondary" (click)="reset()">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
          Reset
        </button>
      }
      @if (showFormErrors && !isFormValid) {
        <span class="form-error-hint">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Fix the errors above to calculate
        </span>
      }
    </div>
  </div>

  @if (calculated && result) {
    <div class="summary-grid mb-md">
      <div class="summary-card c-blue">
        <div class="sc-icon"></div>
        <div class="sc-label">CAGR</div>
        <div class="sc-value">{{ result.cagrPercent.toFixed(2) }}%</div>
        <div class="sc-sub">compound annual growth rate</div>
      </div>
      <div class="summary-card c-green">
        <div class="sc-icon"></div>
        <div class="sc-label">Absolute Return</div>
        <div class="sc-value">{{ result.absoluteReturnPercent.toFixed(2) }}%</div>
        <div class="sc-sub">over {{ years }} years</div>
      </div>
      <div class="summary-card c-red">
        <div class="sc-icon"></div>
        <div class="sc-label">Total Gain</div>
        <div class="sc-value">{{ fmt.currency(result.totalGain) }}</div>
        <div class="sc-sub">{{ fmt.currency(initialValue) }} → {{ fmt.currency(finalValue) }}</div>
      </div>
    </div>

    <div class="card charts-card mb-md">
      <div class="section-title">Implied Growth Path</div>
      <div class="chart-panel">
        <div class="chart-canvas-wrap">
          <canvas #growthRef></canvas>
        </div>
      </div>
    </div>

    <div class="card mb-md">
      <div class="section-title">Year-wise Value (at CAGR)</div>
      <div class="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th class="t-left">Year</th>
              <th class="t-balance">Value</th>
            </tr>
          </thead>
          <tbody>
            @for (row of result.years; track row.year) {
              <tr>
                <td class="t-left t-year">Year {{ row.year }}</td>
                <td class="t-balance">{{ fmt.currency(row.value) }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  }
</div>

<!-- ══ SEO CONTENT ══════════════════════════════════════════════════════ -->
<section class="container seo-content">
  <div class="card">
    <h2 class="section-title">How is CAGR Calculated?</h2>
    <p>
      CAGR (Compound Annual Growth Rate) is the constant annual rate at which an investment would
      have grown from its initial value to its final value, smoothing out the actual year-to-year
      volatility into a single average rate.
    </p>
    <p class="emi-formula">CAGR = (Final Value / Initial Value)<sup>1/n</sup> &minus; 1</p>
    <ul class="emi-formula-vars">
      <li><strong>Final Value</strong> &mdash; The value at the end of the period</li>
      <li><strong>Initial Value</strong> &mdash; The value at the start of the period</li>
      <li><strong>n</strong> &mdash; Number of years</li>
    </ul>
    <p>
      For example, an investment that grows from &#8377;1,00,000 to &#8377;2,00,000 over 5 years
      has a CAGR of about 14.87%, even though the actual year-to-year returns may have varied widely.
    </p>

    <h2 class="section-title mt-sm">Frequently Asked Questions</h2>
    <div class="faq-item">
      <h3>How is CAGR different from absolute return?</h3>
      <p>
        Absolute return is the total percentage gain over the entire period, regardless of how long
        it took. CAGR annualizes that gain, making it possible to fairly compare investments held
        for different durations.
      </p>
    </div>
    <div class="faq-item">
      <h3>Does CAGR reflect actual year-to-year performance?</h3>
      <p>
        No. CAGR is a smoothed, hypothetical rate. An investment could have had sharp ups and downs
        along the way and still arrive at the same CAGR based purely on its start and end values.
      </p>
    </div>
    <p class="learn-crosslink">
      Read more: <a routerLink="/learn/cagr-vs-xirr">CAGR vs XIRR: which one should you use for SIP returns?</a>
    </p>
    <p class="learn-crosslink">
      Also see: <a routerLink="/sip">SIP Calculator</a> and
      <a routerLink="/lumpsum">Lumpsum Calculator</a>
    </p>
  </div>
</section>
